import React, { useMemo } from "react";

const PLACEHOLDER =
  "https://static.vecteezy.com/system/resources/previews/005/337/799/original/icon-image-not-found-free-vector.jpg";

const PlayIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
    <path d="M8 5.14v13.72L19 12 8 5.14z" fill="currentColor" />
  </svg>
);

const PhotosIcon = () => (
  <svg width="15" height="15" viewBox="0 0 16 16" fill="none" aria-hidden>
    <rect x="1" y="3" width="10" height="10" rx="1.5" stroke="currentColor" strokeWidth="1.4" />
    <rect x="4" y="1" width="10" height="10" rx="1.5" fill="currentColor" opacity="0.35" />
    <rect x="4" y="1" width="10" height="10" rx="1.5" stroke="currentColor" strokeWidth="1.4" />
  </svg>
);

const MediaFill = ({ src, alt, isVideo = false }) => {
  if (isVideo && src) {
    return (
      <video
        src={src}
        className="gallery-sv__media"
        autoPlay
        muted
        loop
        playsInline
        preload="metadata"
        aria-label={alt}
      />
    );
  }
  return (
    <img
      src={src || PLACEHOLDER}
      alt={alt}
      className="gallery-sv__media"
      loading="lazy"
    />
  );
};

/**
 * StayVista-style collage:
 * - Large left photo
 * - Top-right: autoplaying muted video when property has video(s), else photo
 * - Bottom-right: photo + "+N More"
 * - "View Video" only when property has video(s)
 */
const HeroCollage = ({
  items = [],
  onOpenAt,
  onShowAll,
  onViewVideo,
}) => {
  const photos = useMemo(
    () => items.filter((item) => item && !item.isVideo),
    [items]
  );
  const videos = useMemo(
    () => items.filter((item) => item && item.isVideo),
    [items]
  );
  const hasVideo = videos.length > 0;
  const firstVideo = videos[0] || null;

  // Prefer photos for still tiles; fall back to any media
  const collagePool = photos.length > 0 ? photos : items;
  const main = collagePool[0] || null;
  // Top-right prefers autoplaying video when available
  const sideTop = firstVideo || collagePool[1] || collagePool[0] || null;
  const sideBottom =
    collagePool[hasVideo ? 1 : 2] ||
    collagePool[1] ||
    collagePool[0] ||
    null;
  const extraCount = Math.max(items.length - 3, 0);

  const openPhotoAtPoolIndex = (poolIndex) => {
    if (!items.length) {
      onShowAll?.();
      return;
    }
    const target = collagePool[poolIndex];
    if (!target) {
      onShowAll?.();
      return;
    }
    const absoluteIndex = items.findIndex((item) => item.src === target.src);
    onOpenAt?.(absoluteIndex >= 0 ? absoluteIndex : 0);
  };

  const handleViewVideo = (e) => {
    e.stopPropagation();
    if (!hasVideo) return;
    if (onViewVideo) {
      onViewVideo(videos[0]);
      return;
    }
    const absoluteIndex = items.findIndex((item) => item.isVideo);
    if (absoluteIndex >= 0) onOpenAt?.(absoluteIndex);
  };

  const handleSideTopClick = () => {
    if (hasVideo && firstVideo) {
      if (onViewVideo) {
        onViewVideo(firstVideo);
        return;
      }
      const absoluteIndex = items.findIndex((item) => item.isVideo);
      if (absoluteIndex >= 0) {
        onOpenAt?.(absoluteIndex);
        return;
      }
    }
    openPhotoAtPoolIndex(1);
  };

  const handleViewPhotos = (e) => {
    e.stopPropagation();
    onShowAll?.();
  };

  return (
    <div className="gallery-sv">
      <button
        type="button"
        className="gallery-sv__main"
        onClick={() => openPhotoAtPoolIndex(0)}
        aria-label="View main photo"
      >
        <MediaFill src={main?.src} alt="Property main" />

        <div className="gallery-sv__actions">
          {hasVideo ? (
            <button
              type="button"
              className="gallery-sv__btn"
              onClick={handleViewVideo}
            >
              <PlayIcon />
              <span className="gallery-sv__btn-label">View Video</span>
            </button>
          ) : null}
          <button
            type="button"
            className="gallery-sv__btn"
            onClick={handleViewPhotos}
          >
            <PhotosIcon />
            <span className="gallery-sv__btn-label">View Photos</span>
          </button>
        </div>
      </button>

      <button
        type="button"
        className="gallery-sv__side gallery-sv__side--top"
        onClick={handleSideTopClick}
        aria-label={hasVideo ? "View property video" : "View photo 2"}
      >
        <MediaFill
          src={sideTop?.src}
          alt={hasVideo ? "Property video" : "Property 2"}
          isVideo={Boolean(hasVideo && sideTop?.isVideo)}
        />
      </button>

      <button
        type="button"
        className="gallery-sv__side gallery-sv__side--bottom"
        onClick={() => onShowAll?.()}
        aria-label="Show all photos"
      >
        <MediaFill src={sideBottom?.src} alt="Property 3" />
        {extraCount > 0 ? (
          <span className="gallery-sv__more">+{extraCount} More</span>
        ) : (
          <span className="gallery-sv__more" style={{ fontSize: "16px" }}>
            View all
          </span>
        )}
      </button>
    </div>
  );
};

export default HeroCollage;
